import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Progress,
  Avatar,
  Tooltip,
  Popconfirm,
  Modal,
} from "antd";
import {
  FaTasks,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaProjectDiagram,
  FaUser,
  FaClock,
  FaExclamationCircle,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import "./AprovacaoTarefas.css";

const { Content } = Layout;
const { Title, Text } = Typography;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "PENDENTE", color: "orange", icon: <FaClock /> },
    2: { label: "APROVADA", color: "green", icon: <FaCheckCircle /> },
    3: { label: "REJEITADA", color: "red", icon: <FaTimesCircle /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", icon: null };
};

/* ─────────────────────────────────────────────────────────────────────────────
   PÁGINA DE APROVAÇÃO DE TAREFAS
───────────────────────────────────────────────────────────────────────────── */
const AprovacaoTarefas = () => {
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [processingIds, setProcessingIds] = useState([]);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Aprovação de Tarefas | Dashboard";
  }, []);

  const loadTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListTarefaAprovacao({ id_direcao: userStored.direcao });
      const list = Array.isArray(response) ? response : [];
      setTarefas(list);
    } catch {
      toast.error("Erro ao carregar tarefas para aprovação.");
      setTarefas([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.direcao]);

  useEffect(() => {
    loadTarefas();
  }, [loadTarefas]);

  const handleAprovar = async (id) => {
    try {
      setProcessingIds(prev => [...prev, id]);
      await Api.AprovarTarefa({ id, id_status: 2 });
      toast.success("Tarefa aprovada com sucesso!");
      loadTarefas();
    } catch {
      toast.error("Erro ao aprovar tarefa.");
    } finally {
      setProcessingIds(prev => prev.filter(pid => pid !== id));
    }
  };

  const handleRejeitar = async (id) => {
    try {
      setProcessingIds(prev => [...prev, id]);
      await Api.AprovarTarefa({ id, id_status: 1 });
      toast.success("Tarefa rejeitada com sucesso!");
      loadTarefas();
    } catch {
      toast.error("Erro ao rejeitar tarefa.");
    } finally {
      setProcessingIds(prev => prev.filter(pid => pid !== id));
    }
  };

  // Estatísticas
  const totalPendentes = tarefas.length;
  const tarefasAprovadasHoje = 0; // Poderia ser calculado se houvesse data de aprovação

  // Filtragem
  const filtered = tarefas.filter((t) => {
    const q = search.toLowerCase();
    return (
      !q ||
      t.titulo?.toLowerCase().includes(q) ||
      t.descricao?.toLowerCase().includes(q) ||
      t.projeto?.toLowerCase().includes(q) ||
      t.nome?.toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      title: "Tarefa",
      dataIndex: "titulo",
      key: "titulo",
      width: 280,
      render: (titulo, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{
              backgroundColor: "#fff7e6",
              color: "#1677ff",
              borderRadius: 8,
            }}
            icon={<FaTasks />}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
              {titulo}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.descricao?.length > 60
                ? record.descricao.substring(0, 60) + "..."
                : record.descricao}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Projeto",
      dataIndex: "projeto",
      key: "projeto",
      width: 200,
      render: (projeto) => (
        <Tag color="blue" style={{ borderRadius: 20, padding: "4px 12px" }}>
          <FaProjectDiagram style={{ marginRight: 6 }} />
          {projeto}
        </Tag>
      ),
    },
    {
      title: "Responsável",
      dataIndex: "nome",
      key: "nome",
      width: 180,
      render: (nome) => (
        <Tag color="purple" style={{ borderRadius: 20, padding: "4px 12px" }}>
          <FaUser style={{ marginRight: 6 }} />
          {nome}
        </Tag>
      ),
    },
    {
      title: "Período",
      key: "periodo",
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.inicio)}
          </Text>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {formatDate(record.fim)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Progresso",
      dataIndex: "percentagem",
      key: "percentagem",
      width: 180,
      render: (percentagem) => {
        const valor = parseFloat(percentagem) || 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={valor}
              size="small"
              status={valor === 100 ? "success" : "active"}
              style={{ flex: 1 }}
              strokeColor={{
                "0%": "#1677ff",
                "100%": "#52c41a",
              }}
            />
            <Text strong style={{ minWidth: 45, textAlign: "right" }}>
              {valor}%
            </Text>
          </div>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_, record) => {
        const isProcessing = processingIds.includes(record.id);
        return (
          <Space>
            <Popconfirm
              title="Aprovar tarefa"
              description="Confirma a aprovação desta tarefa?"
              onConfirm={() => handleAprovar(record.id)}
              okText="Sim"
              cancelText="Não"
              okButtonProps={{ loading: isProcessing }}
            >
              <Tooltip title="Aprovar">
                <Button
                  type="text"
                  icon={<FaCheckCircle />}
                  style={{ color: "#52c41a" }}
                  loading={isProcessing}
                />
              </Tooltip>
            </Popconfirm>
            <Popconfirm
              title="Rejeitar tarefa"
              description="Confirma a rejeição desta tarefa?"
              onConfirm={() => handleRejeitar(record.id)}
              okText="Sim"
              cancelText="Não"
              okButtonProps={{ danger: true, loading: isProcessing }}
            >
              <Tooltip title="Rejeitar">
                <Button
                  type="text"
                  icon={<FaTimesCircle />}
                  style={{ color: "#ff4d4f" }}
                  loading={isProcessing}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Layout className="aprovacao-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho com gradiente laranja/âmbar */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(250, 140, 22, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaTasks style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Aprovação de Tarefas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Revise e valide as entregas da sua equipe
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>
        </div>

         
        {/* Barra de pesquisa */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por título, projeto ou responsável..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
          </Row>
        </Card>

        {/* Tabela de tarefas pendentes */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} tarefas`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhuma tarefa pendente de aprovação" }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "table-row-light" : "table-row-dark"
            }
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default AprovacaoTarefas;